import { Toaster } from '@themenu/design-system'

import {
  createContext,
  type PropsWithChildren,
  useMemo,
  useState,
  useEffect
} from 'react'

import {
  GLOBAL_TOAST_EVENT_NAME,
  dispatchToaster,
  type DispatchToastProps
} from '../utils/notifications'

type NotificationContextProps = {
  dispatchToaster: (props: DispatchToastProps) => void
}

const NotificationContext = createContext<NotificationContextProps | null>(null)

type NotificationProviderProps = object & PropsWithChildren

export function NotificationProvider({ children }: NotificationProviderProps) {
  const [globalToasterOpen, setGlobalToasterOpen] = useState(false)
  const [globalToasterDuration, setGlobalToasterDuration] = useState(5000)
  const [globalToasterAutoHide, setGlobalToasterAutoHide] = useState(false)
  const [globalToasterTitle, setGlobalToasterTitle] = useState('')
  const [globalToasterMessage, setGlobalToasterMessage] = useState<
    string | undefined
  >(undefined)
  const [globalToasterVariant, setGlobalToasterVariant] = useState<
    'success' | 'error'
  >('success')

  const handleDispatchToaster = ({
    detail: {
      title,
      message = undefined,
      variant = 'success',
      autoHide,
      autoHideDuration = 5000
    }
  }: CustomEvent<DispatchToastProps>) => {
    setGlobalToasterOpen(true)
    setGlobalToasterTitle(title)
    setGlobalToasterMessage(message)
    setGlobalToasterVariant(variant)
    setGlobalToasterAutoHide(autoHide ?? true)
    setGlobalToasterDuration(autoHideDuration ?? true)
  }

  useEffect(() => {
    window.addEventListener(
      GLOBAL_TOAST_EVENT_NAME,
      handleDispatchToaster as EventListener
    )
  }, [])

  const handleResetToaster = () => {
    setGlobalToasterOpen(false)
    setGlobalToasterTitle('')
    setGlobalToasterMessage(undefined)
    setGlobalToasterVariant('success')
    setGlobalToasterAutoHide(true)
    setGlobalToasterDuration(5000)
  }

  const value = useMemo(
    () => ({
      dispatchToaster
    }),
    []
  )

  return (
    <NotificationContext.Provider value={value}>
      {children}

      {globalToasterOpen && (
        <Toaster
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          role="alert"
          zIndex={1000}
          autoHide={globalToasterAutoHide}
          autoHideDuration={globalToasterDuration}
          onClose={handleResetToaster}
          open={globalToasterOpen}
          positionOptions={{
            bottom: undefined,
            left: undefined,
            right: '0px',
            top: '0'
          }}
          aria-label={globalToasterTitle}
          title={globalToasterTitle}
          secondaryText={globalToasterMessage}
          variant={globalToasterVariant}
        />
      )}
    </NotificationContext.Provider>
  )
}
